import { atom } from 'jotai';

// =========================================================
// Signing in again after a WebEAM token expired
// =========================================================
//
// WebEAM tokens can be short-lived (two minutes on the BMW integration
// system) and come without a refresh token. The backend answers an expired
// one with 403, and WebEAM usually still holds its own session then, so a new
// SSO flow brings a new token without asking the user.
//
// The flow redirects to WebEAM and back, so a guard kept in memory would be
// gone afterwards. `sessionStorage` survives the redirect and belongs to the
// tab. A token expires at the earliest two minutes after its login, while a
// 403 that is not about the token, such as one for an expired account, comes
// right after a fresh login again; one automatic re-login a minute tells them
// apart without redirecting in a loop.

const LAST_RELOGIN_KEY = 'EKKO_WORKERUI_AUTOMATIC_RELOGIN';
const MIN_INTERVAL_MS = 60_000;

/**
 * Set by `AuthGuard` when it logs out after a 403 and an automatic re-login
 * may follow; the login page starts it and resets the flag.
 */
export const automaticReloginRequestedAtom = atom(false);

/**
 * Claims the automatic re-login of this tab, if none ran within the last
 * minute. Without storage it refuses, as nothing could stop a loop then.
 */
export function claimAutomaticRelogin(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(LAST_RELOGIN_KEY));
    if (Number.isFinite(last) && now - last < MIN_INTERVAL_MS) {
      return false;
    }

    sessionStorage.setItem(LAST_RELOGIN_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}
